<template>
  <main class="sms-page">
    <header class="page-heading">
      <div>
        <h1><span class="title-bar"></span>短信通知</h1>
        <p>{{ activeTab === 'records' ? '查看短信发送、提交与最终投递状态，失败记录可补发。' : '配置活动短信模板、发送规则和余额提醒阈值。' }}</p>
      </div>
    </header>

    <section v-if="!activityId" class="gate-card">
      <el-empty description="请先保存活动基础信息，再配置短信通知">
        <el-button type="primary" @click="goEventInfo">前往活动信息</el-button>
      </el-empty>
    </section>

    <template v-else>
      <nav class="tabs" aria-label="短信通知页面">
        <button :class="{ selected: activeTab === 'records' }" @click="activeTab = 'records'">短信通知记录</button>
        <button :class="{ selected: activeTab === 'settings' }" @click="activeTab = 'settings'">短信通知配置</button>
      </nav>

      <template v-if="activeTab === 'records'">
        <section class="records-panel" v-loading="recordsLoading">
          <section class="sms-rec-metrics">
            <div class="sms-rec-metric">
              <div class="sms-rec-metric-v">{{ recordSummary.todayUnits }}</div>
              <div class="sms-rec-metric-l">今日发送（条）</div>
            </div>
            <div class="sms-rec-metric">
              <div class="sms-rec-metric-v failure">{{ failureRateLabel }}</div>
              <div class="sms-rec-metric-l">失败率（共 {{ recordSummary.failed }} 条失败）</div>
            </div>
            <div class="sms-rec-metric">
              <div class="sms-rec-metric-v">{{ balanceLabel }}</div>
              <div class="sms-rec-metric-l">
                短信包剩余（条）
                <button class="sms-btn-sm primary" type="button" @click="goRechargePackages">去充值短信包</button>
              </div>
              <div v-if="!overview.hasSmsPackage" class="sms-no-package">
                当前企业尚未购买短信套餐，短信不会发送，请先去充值套餐购买。
              </div>
            </div>
          </section>

          <section class="content-card">
            <div class="status-tabs">
              <button
                v-for="tab in statusTabs"
                :key="tab.key"
                :class="{ selected: statusFilter === tab.key }"
                @click="statusFilter = tab.key"
              >
                {{ tab.label }} <small>{{ tab.count }}</small>
              </button>
            </div>

            <div class="filters">
              <select v-model="templateFilter">
                <option value="all">全部模板</option>
                <option v-for="item in templates" :key="item.key" :value="item.key">{{ item.name }}</option>
              </select>
              <select v-model="sourceFilter">
                <option value="all">全部类型</option>
                <option value="automatic">自动</option>
                <option value="manual">手动</option>
                <option value="resend">补发</option>
              </select>
              <select v-model="timeFilter">
                <option value="all">全部时间</option>
                <option value="week">近 7 天</option>
                <option value="month">近 30 天</option>
              </select>
              <button class="plain-button" type="button" :disabled="exportingCsv" @click="exportCsv">
                {{ exportingCsv ? '导出中' : '导出 CSV' }}
              </button>
              <label class="search-box">
                <span>🔍</span>
                <input v-model.trim="searchText" placeholder="搜索接收人 / 手机号 / 模板" @keyup.enter="reloadRecordsFromFirstPage">
              </label>
            </div>

            <div v-if="recordsError" class="error-state">
              <span>{{ recordsError }}</span>
              <button type="button" @click="loadRecords">重新加载</button>
            </div>

            <div class="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>发送时间</th>
                    <th>类型</th>
                    <th>通知模板</th>
                    <th>接收人</th>
                    <th>条数</th>
                    <th>状态</th>
                    <th>操作</th>
                  </tr>
                </thead>
                <tbody>
                  <template v-for="row in recordRows" :key="row.id">
                    <tr
                      class="record-row"
                      :class="{ failed: isFailureStatus(row.status), expanded: expandedId === row.id }"
                      @click="toggleExpanded(row.id)"
                    >
                      <!-- 发送时间优先取实际发出时间，未发出的回落到创建时间 -->
                      <td>{{ formatDateTime(row.sentAt || row.createdAt) }}</td>
                      <td><span class="type-pill" :class="row.source || 'automatic'">{{ sourceLabel(row.source) }}</span></td>
                      <td>{{ row.templateName }}</td>
                      <td>{{ row.recipientName || '—' }}（{{ row.phoneNumber || row.phoneMasked || '未提供' }}）</td>
                      <td>{{ row.smsUnits }} 条</td>
                      <td><span class="state" :class="statusClass(row.status)">● {{ statusLabel(row.status) }}</span></td>
                      <td @click.stop>
                        <button
                          v-if="canResend(row.status)"
                          class="link-button"
                          type="button"
                          @click="openResend(row)"
                        >补发</button>
                        <button
                          v-if="row.status === 'unknown'"
                          class="link-button secondary"
                          type="button"
                          :disabled="queryingId === row.id"
                          @click="queryStatus(row)"
                        >{{ queryingId === row.id ? '查询中' : '查询状态' }}</button>
                        <span v-if="!canResend(row.status) && row.status !== 'unknown'" class="muted">—</span>
                      </td>
                    </tr>
                    <tr v-if="expandedId === row.id" class="detail-row" :class="{ failed: isFailureStatus(row.status) }">
                      <td colspan="7">
                        <div v-if="detailReason(row)" class="detail-reason">
                          <strong>失败原因：</strong><span>{{ detailReason(row) }}</span>
                        </div>
                        <div class="detail-content"><strong>短信内容：</strong><span>{{ row.content || '暂无内容' }}</span></div>
                      </td>
                    </tr>
                  </template>
                  <tr v-if="!recordsLoading && !recordRows.length">
                    <td colspan="7" class="empty-cell">暂无符合条件的短信记录</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <footer class="table-footer">
              <span></span>
              <div class="pagination">
                <span class="pagination-summary">共 {{ recordsMeta.total }} 条 · 第 {{ recordsMeta.currentPage }} / {{ recordsMeta.lastPage }} 页</span>
                <button :disabled="recordsMeta.currentPage <= 1" @click="changePage(recordsMeta.currentPage - 1)">上一页</button>
                <button :disabled="recordsMeta.currentPage >= recordsMeta.lastPage" @click="changePage(recordsMeta.currentPage + 1)">下一页</button>
              </div>
            </footer>
          </section>
        </section>
      </template>

      <template v-else>
        <section class="sms-overview" v-loading="overviewLoading">
          <div class="sms-ov-info">
            <div class="sms-ov-copy">
              <div class="sms-ov-title">通知总开关</div>
              <div class="sms-ov-status">
                <span :class="overview.enabled ? 'on' : 'off-text'">{{ overview.enabled ? '开启中' : '已关闭' }}</span>
                · {{ overview.enabled ? '自动任务、手动发送和补发均可用' : '已暂停所有短信通知' }}
              </div>
            </div>
            <label class="tog" aria-label="短信通知总开关">
              <input v-model="overview.enabled" type="checkbox" @change="scheduleOverviewSave">
              <span class="tog-slider"></span>
            </label>
          </div>
          <div class="sms-ov-right">
            <div class="sms-stat"><div class="sms-stat-v">{{ balanceLabel }}</div><div class="sms-stat-l">短信包剩余（条）</div></div>
            <div class="sms-stat">
              <div class="sms-stat-l threshold-label">余额预警阈值</div>
              <label class="sms-stat-input">
                <input v-model.number="overview.balanceWarningThreshold" class="inp" type="number" min="0" @input="scheduleOverviewSave">
                <span>条</span>
              </label>
            </div>
          </div>
        </section>

        <section class="template-grid">
          <article
            v-for="item in templates"
            :key="item.key"
            class="template-card"
            :class="{ editing: editingKey === item.key }"
          >
            <header>
              <span class="green-dot" :class="{ off: !item.enabled }"></span>
              <strong>{{ item.name }}</strong>
              <label class="switch small">
                <input
                  v-model="item.enabled"
                  type="checkbox"
                  :disabled="!overview.enabled || templateSaving"
                  :title="overview.enabled ? (item.enabled ? '点击停用该模板' : '点击开启该模板') : '活动短信通知总开关已关闭'"
                  @change="toggleTemplateEnabled(item)"
                >
                <i></i>
              </label>
            </header>
            <pre class="template-content">{{ item.content }}</pre>
            <div class="template-options">
              <label>
                发送时机
                <select
                  :value="timingSelectValue(item)"
                  :disabled="editingKey !== item.key || !overview.enabled || item.lockedTiming"
                  @change="onTimingChange(item, $event.target.value)"
                >
                  <option v-if="item.lockedTiming" :value="`event:${item.timingType}`">{{ timingLabel(item) }}</option>
                  <template v-else>
                    <option value="immediate">即时触发</option>
                    <option v-for="option in timingOptions(item)" :key="option.value" :value="option.value">
                      {{ option.label }}
                    </option>
                    <option value="manual">仅手动发送</option>
                  </template>
                </select>
              </label>
              <label>
                发送对象
                <select
                  v-model="item.audienceType"
                  :disabled="editingKey !== item.key || !overview.enabled || audienceOptions(item).length <= 1"
                >
                  <option v-for="option in audienceOptions(item)" :key="option.value" :value="option.value">
                    {{ option.label }}
                  </option>
                </select>
              </label>
              <label class="manual-check">
                <input
                  v-model="item.allowManual"
                  type="checkbox"
                  :disabled="editingKey !== item.key || !overview.enabled || item.lockedTiming"
                >
                允许手动发送
              </label>
            </div>
            <footer v-if="editingKey === item.key" class="editing-actions">
              <button class="primary-button" :disabled="templateSaving" @click="saveTemplate(item)">{{ templateSaving ? '保存中' : '保存' }}</button>
              <button class="plain-button" @click="cancelEdit(item)">取消</button>
              <button class="plain-button" :disabled="!canManualSend(item)" :title="manualSendTitle(item)" @click="openManualSend(item)">手动发送</button>
            </footer>
            <footer v-else class="card-actions">
              <button class="plain-button" @click="startEdit(item)">编辑</button>
              <button class="primary-button" :disabled="!canManualSend(item)" :title="manualSendTitle(item)" @click="openManualSend(item)">手动发送</button>
            </footer>
          </article>
        </section>
      </template>
    </template>

    <div v-if="manualSendItem" class="modal-backdrop" @click.self="closeManualSend">
      <section class="manual-send-modal" role="dialog" aria-modal="true">
        <header>
          <h2>手动发送 · {{ manualSendItem.name }}</h2>
          <button class="close-button" aria-label="关闭" @click="closeManualSend">×</button>
        </header>
        <div class="manual-send-intro">{{ manualSendIntro }}</div>
        <div class="manual-send-body" v-loading="recipientsLoading">
          <div class="manual-tools">
            <label class="manual-search">
              <input v-model.trim="manualSearch" placeholder="搜索姓名或手机号" @keyup.enter="searchRecipients">
              <span>⌕</span>
            </label>
            <button class="plain-button" @click="searchRecipients">搜索</button>
          </div>
          <div class="manual-select-all">
            <label><input type="checkbox" :checked="allPageSelected" @change="togglePageAll"> 全选当前页</label>
            <span>已选 {{ manualSelected.length }} 人</span>
          </div>
          <div class="participant-list">
            <label
              v-for="person in recipientRows"
              :key="person.recipientId || person.submissionId"
              class="participant-row"
              :class="{ disabled: !person.canReceive }"
            >
              <input
                v-model="manualSelected"
                type="checkbox"
                :value="person.recipientId || person.submissionId"
                :disabled="!person.canReceive"
              >
              <strong>{{ person.name || '未填写姓名' }}</strong>
              <span>{{ person.phoneMasked || '手机号缺失' }}</span>
              <small>{{ person.canReceive ? '可发送' : recipientReasonLabel(person.reason) }}</small>
            </label>
            <div v-if="!recipientsLoading && !recipientRows.length" class="empty-cell">没有符合条件的接收人</div>
          </div>
          <div class="modal-pagination">
            <span>共 {{ recipientMeta.total }} 人 · 第 {{ recipientMeta.currentPage }} / {{ recipientMeta.lastPage }} 页</span>
            <button :disabled="recipientMeta.currentPage <= 1" @click="changeRecipientPage(recipientMeta.currentPage - 1)">上一页</button>
            <button :disabled="recipientMeta.currentPage >= recipientMeta.lastPage" @click="changeRecipientPage(recipientMeta.currentPage + 1)">下一页</button>
          </div>
        </div>
        <footer>
          <button class="plain-button" @click="closeManualSend">取消</button>
          <button class="primary-button" :disabled="manualSubmitting || !manualSelected.length" @click="submitManualSend">
            {{ manualSubmitting ? '提交中' : `发送（${manualSelected.length}）` }}
          </button>
        </footer>
      </section>
    </div>

    <div v-if="resendRow" class="modal-backdrop" @click.self="closeResend">
      <section class="resend-modal" role="dialog" aria-modal="true">
        <header>
          <h2>补发短信 · {{ resendRow.templateName }}</h2>
          <button class="close-button" aria-label="关闭" @click="closeResend">×</button>
        </header>
        <div class="modal-body">
          <div><span>原接收人</span><strong>{{ resendRow.recipientName || '—' }}</strong></div>
          <label><span>手机号</span><input v-model.trim="resendPhone" inputmode="tel" placeholder="留空则使用原手机号"></label>
          <div><span>失败原因</span><strong class="error-text">{{ detailReason(resendRow) || '—' }}</strong></div>
        </div>
        <footer>
          <button class="plain-button" @click="closeResend">取消</button>
          <button class="primary-button" :disabled="resendSubmitting" @click="submitResend">
            {{ resendSubmitting ? '提交中' : '确认补发' }}
          </button>
        </footer>
      </section>
    </div>
  </main>
</template>

<script>
import { computed, onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { ElMessage } from 'element-plus'
import { useActivityIdFromRouteOrSession } from '@/composables/useActivityIdFromRouteOrSession'
import {
  createActivitySmsBatch,
  getActivitySmsSettings,
  listActivitySmsRecipients,
  listActivitySmsRecords,
  queryActivitySmsRecordStatus,
  resendActivitySmsRecord,
  updateActivitySmsSettings,
  updateActivitySmsTemplate
} from '@/api/activitySms'

// 记录列表只对外展示三个状态：成功 / 失败 / 发送中。
const STATUS_DISPLAY = {
  pending: '发送中',
  queued: '发送中',
  sending: '发送中',
  sent: '发送中',
  delivered: '成功',
  submit_failed: '失败',
  failed: '失败',
  unknown: '失败',
  skipped: '失败'
}

const AUDIENCE_LABELS = {
  registered_attendee: '全部成功报名人',
  registrant: '当前报名人',
  reviewed_registrant: '本次审核报名人',
  seat_assigned_registrant: '已分配座位报名人',
  hotel_assigned_registrant: '已分配酒店报名人',
  checked_in_registrant: '已完成签到报名人'
}

const RECIPIENT_REASON_LABELS = {
  phone_missing: '手机号缺失',
  phone_invalid: '手机号格式错误'
}

const AUDIENCE_OPTIONS = {
  registration: [{ value: 'registrant', label: '当前报名人' }],
  registration_confirmed: [{ value: 'registrant', label: '当前报名人' }],
  registration_approved: [{ value: 'reviewed_registrant', label: '本次审核报名人' }],
  registration_rejected: [{ value: 'reviewed_registrant', label: '本次审核报名人' }],
  start: [{ value: 'registered_attendee', label: '报名该会议人员' }],
  seat: [{ value: 'seat_assigned_registrant', label: '座位表人员' }],
  hotel: [{ value: 'hotel_assigned_registrant', label: '已分配酒店报名人' }],
  checkin: [{ value: 'checked_in_registrant', label: '签到名单人员' }],
  agenda: [
    { value: 'registered_attendee', label: '报名该会议人员' },
    { value: 'checked_in_registrant', label: '报名该会议且已签到人员' }
  ],
  dining: [
    { value: 'registered_attendee', label: '全部成功报名人' },
    { value: 'checked_in_registrant', label: '已完成签到报名人' }
  ]
}

function createIdempotencyKey (prefix) {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return `${prefix}:${crypto.randomUUID()}`
  return `${prefix}:${Date.now()}:${Math.random().toString(36).slice(2)}`
}

export default {
  name: 'EventSmsNotificationsView',
  setup () {
    const route = useRoute()
    const router = useRouter()
    const { activityId } = useActivityIdFromRouteOrSession(route)
    const activeTab = ref('records')
    const overviewLoading = ref(false)
    const overviewSaving = ref(false)
    const recordsLoading = ref(false)
    const recordsError = ref('')
    const exportingCsv = ref(false)
    const templateSaving = ref(false)
    const queryingId = ref(null)
    const overview = reactive({
      enabled: false,
      balanceWarningThreshold: 200,
      smsBalance: { remainingUnits: 0, unlimited: false },
      // 企业是否买过短信套餐；没买过时禁用发送入口
      hasSmsPackage: true
    })
    let autosaveTimer = null
    const templates = ref([])
    const editingKey = ref('')
    const editSnapshot = ref(null)
    const statusFilter = ref('all')
    const templateFilter = ref('all')
    const sourceFilter = ref('all')
    const timeFilter = ref('all')
    const searchText = ref('')
    const recordRows = ref([])
    const recordsMeta = reactive({ currentPage: 1, perPage: 10, total: 0, lastPage: 1 })
    const recordSummary = reactive({ total: 0, success: 0, failed: 0, pending: 0, todayUnits: 0 })
    const expandedId = ref(null)
    const manualSendItem = ref(null)
    const recipientsLoading = ref(false)
    const recipientRows = ref([])
    const manualSearch = ref('')
    const manualSelected = ref([])
    const recipientMeta = reactive({ currentPage: 1, perPage: 10, total: 0, lastPage: 1 })
    const manualSubmitting = ref(false)
    const resendRow = ref(null)
    const resendPhone = ref('')
    const resendSubmitting = ref(false)

    const balanceLabel = computed(() => overview.smsBalance?.unlimited
      ? '不限量'
      : Number(overview.smsBalance?.remainingUnits ?? 0).toLocaleString())
    const failureRateLabel = computed(() => {
      const total = Number(recordSummary.total || 0)
      const failed = Number(recordSummary.failed || 0)

      return total > 0 ? `${(failed / total * 100).toFixed(1)}%` : '0.0%'
    })

    const statusTabs = computed(() => [
      { key: 'all', label: '全部', count: recordSummary.total },
      { key: 'success', label: '成功', count: recordSummary.success },
      { key: 'failed', label: '失败', count: recordSummary.failed },
      { key: 'pending', label: '发送中', count: recordSummary.pending }
    ])

    const allPageSelected = computed(() => recipientRows.value.length > 0
      && recipientRows.value.every((item) => !item.canReceive
        || manualSelected.value.includes(item.recipientId || item.submissionId)))

    const goEventInfo = () => router.push('/dashboard/conference/events/create')
    const goRechargePackages = () => router.push('/dashboard/recharge-packages')

    const loadOverview = async () => {
      if (!activityId.value) return
      overviewLoading.value = true
      try {
        const data = await getActivitySmsSettings(activityId.value)
        overview.enabled = Boolean(data?.enabled)
        overview.balanceWarningThreshold = Number(data?.balanceWarningThreshold ?? 200)
        overview.smsBalance = data?.smsBalance || { remainingUnits: 0, unlimited: false }
        // 后端未返回该字段时按"已购买"处理，避免旧版本下误禁用
        overview.hasSmsPackage = data?.hasSmsPackage !== false
        templates.value = Array.isArray(data?.templates) ? data.templates : []
      } catch {
        ElMessage.error('短信配置加载失败')
      } finally {
        overviewLoading.value = false
      }
    }

    const loadRecords = async () => {
      if (!activityId.value) return
      recordsLoading.value = true
      recordsError.value = ''
      try {
        const data = await listActivitySmsRecords(activityId.value, {
          status_group: statusFilter.value === 'all' ? undefined : statusFilter.value,
          template_key: templateFilter.value === 'all' ? undefined : templateFilter.value,
          source: sourceFilter.value === 'all' ? undefined : sourceFilter.value,
          keyword: searchText.value || undefined,
          date_from: dateFromFilter(),
          page: recordsMeta.currentPage,
          per_page: recordsMeta.perPage
        })
        recordRows.value = data.items
        Object.assign(recordsMeta, data.meta)
        Object.assign(recordSummary, {
          total: Number(data.summary?.total ?? data.meta.total ?? 0),
          success: Number(data.summary?.success ?? 0),
          failed: Number(data.summary?.failed ?? 0),
          pending: Number(data.summary?.pending ?? 0),
          todayUnits: Number(data.summary?.todayUnits ?? 0)
        })
      } catch (error) {
        recordsError.value = error?.response?.data?.message || '短信记录加载失败'
      } finally {
        recordsLoading.value = false
      }
    }

    const dateFromFilter = () => {
      if (timeFilter.value === 'week') {
        const date = new Date()
        date.setDate(date.getDate() - 6)
        return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
      }
      if (timeFilter.value === 'month') {
        const date = new Date()
        date.setDate(date.getDate() - 29)
        return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
      }
      return undefined
    }

    const exportCsv = async () => {
      if (!activityId.value || exportingCsv.value) return
      exportingCsv.value = true
      try {
        const rows = []
        let page = 1
        let lastPage = 1
        do {
          const data = await listActivitySmsRecords(activityId.value, {
            status_group: statusFilter.value === 'all' ? undefined : statusFilter.value,
            template_key: templateFilter.value === 'all' ? undefined : templateFilter.value,
            source: sourceFilter.value === 'all' ? undefined : sourceFilter.value,
            keyword: searchText.value || undefined,
            date_from: dateFromFilter(),
            page,
            per_page: 100
          })
          rows.push(...data.items)
          lastPage = Math.max(1, Number(data.meta.lastPage || 1))
          page++
        } while (page <= lastPage && page <= 200)

        if (!rows.length) {
          ElMessage.warning('当前筛选条件下没有可导出的记录')
          return
        }

        const headers = ['发送时间', '类型', '通知模板', '接收人', '手机号', '条数', '状态', '失败原因', '短信内容']
        const csvRows = rows.map((row) => [
          formatDateTime(row.sentAt || row.createdAt),
          sourceLabel(row.source),
          row.templateName || '',
          row.recipientName || '',
          row.phoneMasked || '',
          row.smsUnits || 0,
          statusLabel(row.status),
          detailReason(row) || '',
          row.content || ''
        ])
        const csv = '\uFEFF' + [headers, ...csvRows]
          .map((line) => line.map(csvCell).join(','))
          .join('\r\n')
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
        const url = URL.createObjectURL(blob)
        const link = document.createElement('a')
        const now = new Date()
        link.href = url
        link.download = `短信通知记录_${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}.csv`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        URL.revokeObjectURL(url)
      } catch (error) {
        ElMessage.error(error?.response?.data?.message || '导出失败')
      } finally {
        exportingCsv.value = false
      }
    }

    const csvCell = (value) => {
      let text = String(value ?? '')
      if (/^[=+\-@]/.test(text)) text = `'${text}`
      return `"${text.replace(/"/g, '""')}"`
    }

    const reloadRecordsFromFirstPage = () => {
      recordsMeta.currentPage = 1
      expandedId.value = null
      loadRecords()
    }

    const changePage = (page) => {
      recordsMeta.currentPage = Math.max(1, Math.min(recordsMeta.lastPage, page))
      expandedId.value = null
      loadRecords()
    }

    const scheduleOverviewSave = () => {
      if (autosaveTimer) clearTimeout(autosaveTimer)
      autosaveTimer = setTimeout(() => {
        autosaveTimer = null
        saveOverview()
      }, 800)
    }

    const saveOverview = async () => {
      if (!activityId.value || overviewSaving.value) return
      overviewSaving.value = true
      try {
        await updateActivitySmsSettings(activityId.value, {
          enabled: Boolean(overview.enabled),
          balanceWarningThreshold: Math.max(0, Number(overview.balanceWarningThreshold || 0))
        })
        await loadOverview()
      } catch (error) {
        ElMessage.error(error?.response?.data?.message || '保存失败')
      } finally {
        overviewSaving.value = false
      }
    }

    const startEdit = (item) => {
      editingKey.value = item.key
      editSnapshot.value = { ...item }
    }

    const cancelEdit = (item) => {
      if (editSnapshot.value) Object.assign(item, editSnapshot.value)
      editingKey.value = ''
      editSnapshot.value = null
    }

    const saveTemplate = async (item) => {
      if (!activityId.value || templateSaving.value) return
      templateSaving.value = true
      try {
        const data = await updateActivitySmsTemplate(activityId.value, item.key, {
          enabled: Boolean(item.enabled),
          timingType: item.timingType,
          timingValue: item.timingType === 'before_activity' ? Number(item.timingValue) : null,
          audienceType: item.audienceType,
          allowManual: Boolean(item.allowManual)
        })
        Object.assign(item, data)
        editingKey.value = ''
        editSnapshot.value = null
        ElMessage.success('短信模板已保存')
      } catch (error) {
        ElMessage.error(error?.response?.data?.message || '模板保存失败')
      } finally {
        templateSaving.value = false
      }
    }

    /**
     * 模板卡片右上角的开关：直接点即可开/停用，不需要先进入编辑。
     * 保存失败时把界面状态回滚，避免显示与后端不一致。
     */
    const toggleTemplateEnabled = async (item) => {
      if (!activityId.value || templateSaving.value || !overview.enabled) {
        return
      }
      templateSaving.value = true
      const nextEnabled = Boolean(item.enabled)
      try {
        const data = await updateActivitySmsTemplate(activityId.value, item.key, {
          enabled: nextEnabled,
          timingType: item.timingType,
          timingValue: item.timingType === 'before_activity' ? Number(item.timingValue) : null,
          audienceType: item.audienceType,
          allowManual: Boolean(item.allowManual)
        })
        Object.assign(item, data)
        ElMessage.success(nextEnabled ? `${item.name}已开启` : `${item.name}已停用`)
      } catch (error) {
        item.enabled = !nextEnabled
        ElMessage.error(error?.response?.data?.message || '模板开关保存失败')
      } finally {
        templateSaving.value = false
      }
    }

    // 没买过短信套餐的企业不允许发送：按钮禁用并给出原因。
    const canManualSend = (item) => Boolean(
      overview.enabled && item.enabled && item.allowManual && overview.hasSmsPackage
    )

    const manualSendTitle = (item) => {
      if (!overview.hasSmsPackage) return '当前企业尚未购买短信套餐，请先前往充值套餐购买'
      if (!overview.enabled) return '活动短信通知总开关已关闭'
      if (!item.enabled) return '当前短信模板已停用'
      if (!item.allowManual) return '当前短信模板不允许手动发送'

      return ''
    }

    // 座位通知取座位表、签到通知取签到名单，其余模板取已通过报名。
    const manualSendIntro = computed(() => {
      const key = manualSendItem.value?.key
      if (key === 'seat') return '从座位表中选择接收人，仅手机号有效的人员可发送。'
      if (key === 'checkin') return '从签到名单中选择接收人，仅手机号有效的人员可发送。'

      return '从成功报名记录中选择接收人，仅符合当前模板规则且手机号有效的人员可发送。'
    })

    const openManualSend = async (item) => {
      if (!canManualSend(item)) {
        ElMessage.warning(manualSendTitle(item) || '当前模板不允许手动发送')
        return
      }
      manualSendItem.value = item
      manualSearch.value = ''
      manualSelected.value = []
      recipientMeta.currentPage = 1
      await loadRecipients()
    }

    const closeManualSend = () => {
      manualSendItem.value = null
      recipientRows.value = []
      manualSelected.value = []
    }

    const loadRecipients = async () => {
      if (!activityId.value || !manualSendItem.value) return
      recipientsLoading.value = true
      try {
        const data = await listActivitySmsRecipients(activityId.value, {
          templateKey: manualSendItem.value.key,
          keyword: manualSearch.value || undefined,
          page: recipientMeta.currentPage,
          per_page: recipientMeta.perPage
        })
        recipientRows.value = data.items
        Object.assign(recipientMeta, data.meta)
      } catch (error) {
        ElMessage.error(error?.response?.data?.message || '接收人加载失败')
      } finally {
        recipientsLoading.value = false
      }
    }

    const searchRecipients = () => {
      recipientMeta.currentPage = 1
      loadRecipients()
    }

    const changeRecipientPage = (page) => {
      recipientMeta.currentPage = Math.max(1, Math.min(recipientMeta.lastPage, page))
      loadRecipients()
    }

    const togglePageAll = (event) => {
      const ids = recipientRows.value
        .filter((item) => item.canReceive)
        .map((item) => item.recipientId || item.submissionId)
        .filter(Boolean)
      manualSelected.value = event.target.checked
        ? [...new Set([...manualSelected.value, ...ids])]
        : manualSelected.value.filter((id) => !ids.includes(id))
    }

    const submitManualSend = async () => {
      if (!activityId.value || !manualSendItem.value) return
      const recipientIds = manualSelected.value.filter(Boolean)
      if (!recipientIds.length) {
        ElMessage.warning('请先选择接收人')
        return
      }
      manualSubmitting.value = true
      try {
        const data = await createActivitySmsBatch(activityId.value, {
          templateKey: manualSendItem.value.key,
          recipientIds,
          idempotencyKey: createIdempotencyKey('manual-sms')
        })
        ElMessage.success(`已提交：待发送 ${data?.totalCount ?? recipientIds.length} 条`)
        closeManualSend()
        activeTab.value = 'records'
        recordsMeta.currentPage = 1
        await Promise.all([loadRecords(), loadOverview()])
      } catch (error) {
        ElMessage.error(error?.response?.data?.message || '短信批次提交失败')
      } finally {
        manualSubmitting.value = false
      }
    }

    const openResend = (row) => {
      resendRow.value = row
      resendPhone.value = row.phoneNumber || row.phoneMasked || ''
      expandedId.value = row.id
    }

    const closeResend = () => {
      resendRow.value = null
      resendPhone.value = ''
    }

    const submitResend = async () => {
      if (!activityId.value || !resendRow.value || resendSubmitting.value) return
      resendSubmitting.value = true
      try {
        await resendActivitySmsRecord(activityId.value, resendRow.value.id, {
          phone: resendPhone.value && resendPhone.value !== resendRow.value.phoneNumber
            ? resendPhone.value
            : undefined,
          idempotencyKey: createIdempotencyKey(`resend-${resendRow.value.id}`)
        })
        ElMessage.success('补发任务已创建')
        closeResend()
        await loadRecords()
      } catch (error) {
        ElMessage.error(error?.response?.data?.message || '补发失败')
      } finally {
        resendSubmitting.value = false
      }
    }

    const queryStatus = async (row) => {
      if (!activityId.value || queryingId.value) return
      queryingId.value = row.id
      try {
        const data = await queryActivitySmsRecordStatus(activityId.value, row.id)
        Object.assign(row, data)
        ElMessage.success('短信状态已刷新')
        await loadRecords()
      } catch (error) {
        ElMessage.error(error?.response?.data?.message || '状态查询失败')
      } finally {
        queryingId.value = null
      }
    }

    const toggleExpanded = (id) => {
      expandedId.value = expandedId.value === id ? null : id
    }

    const isFailureStatus = (status) => STATUS_DISPLAY[status] === '失败'
    const statusLabel = (status) => STATUS_DISPLAY[status] || '发送中'
    const statusClass = (status) => {
      if (isFailureStatus(status)) return 'failure'

      return STATUS_DISPLAY[status] === '成功' ? 'success' : 'pending'
    }
    const canResend = (status) => ['submit_failed', 'failed', 'unknown'].includes(status)
    const detailReason = (row) => row.failureReason || (row.statusReason ? `未发送：${row.statusReason}` : '')
    const sourceLabel = (source) => ({ automatic: '自动', manual: '手动', resend: '补发' }[source] || '自动')
    const audienceLabel = (type) => AUDIENCE_LABELS[type] || type || '模板固定规则'
    const audienceOptions = (item) => AUDIENCE_OPTIONS[item.key] || [{ value: item.audienceType, label: audienceLabel(item.audienceType) }]
    const recipientReasonLabel = (reason) => RECIPIENT_REASON_LABELS[reason] || '不可发送'

    const timingLabel = (item) => {
      if (item.timingType === 'immediate') return '即时触发'
      if (item.timingType === 'manual') return '仅手动发送'
      if (item.timingType === 'event') {
        return item.key === 'registration_approved' ? '报名审核通过时即时触发' : '报名审核未通过时即时触发'
      }
      // 会议日程/会议开始通知按会议排期，其余模板按活动时间。
      const isMeetingTemplate = ['agenda', 'start'].includes(item.key)
      const prefix = isMeetingTemplate ? '会议开始前' : '活动开始前'
      const value = Number(item.timingValue)
      if (value === 30) return `${prefix}30分钟`
      if (value === 60) return `${prefix}1小时`
      if (value === 120) return `${prefix}2小时`
      if (value === 1440) return isMeetingTemplate ? '会议前一天' : '活动前一天'

      return prefix
    }

    const timingSelectValue = (item) => {
      if (item.lockedTiming) return `event:${item.timingType}`
      if (item.timingType === 'before_activity') return `before_${item.timingValue}`

      return item.timingType
    }

    // 会议日程/会议开始通知的时机文案按会议描述，其余模板按活动描述。
    const timingOptions = (item) => {
      const prefix = ['agenda', 'start'].includes(item?.key) ? '会议开始前' : '活动开始前'

      return [
        { value: 'before_30', label: `${prefix}30分钟` },
        { value: 'before_60', label: `${prefix}1小时` },
        { value: 'before_120', label: `${prefix}2小时` },
        { value: 'before_1440', label: `${prefix}一天` }
      ]
    }

    const onTimingChange = (item, value) => {
      if (String(value).startsWith('before_')) {
        item.timingType = 'before_activity'
        item.timingValue = Number(String(value).replace('before_', ''))
        return
      }
      item.timingType = value
      item.timingValue = null
    }

    const formatDateTime = (value) => {
      if (!value) return '—'
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return String(value)
      return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')} ${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`
    }

    watch(activityId, async (id) => {
      if (!id) return
      await Promise.all([loadOverview(), loadRecords()])
    })

    watch([statusFilter, templateFilter, sourceFilter, timeFilter], () => reloadRecordsFromFirstPage())
    watch(activeTab, (tab) => {
      if (tab === 'records') loadRecords()
      if (tab === 'settings') loadOverview()
    })

    onMounted(async () => {
      if (!activityId.value) return
      await Promise.all([loadOverview(), loadRecords()])
    })

    onBeforeUnmount(() => {
      if (autosaveTimer) clearTimeout(autosaveTimer)
    })

    return {
      activityId,
      activeTab,
      overview,
      overviewLoading,
      overviewSaving,
      templates,
      editingKey,
      templateSaving,
      statusFilter,
      templateFilter,
      sourceFilter,
      timeFilter,
      searchText,
      recordRows,
      recordsMeta,
      recordSummary,
      statusTabs,
      recordsLoading,
      recordsError,
      exportingCsv,
      expandedId,
      queryingId,
      manualSendItem,
      recipientsLoading,
      recipientRows,
      manualSearch,
      manualSelected,
      recipientMeta,
      manualSubmitting,
      allPageSelected,
      resendRow,
      resendPhone,
      resendSubmitting,
      balanceLabel,
      failureRateLabel,
      goEventInfo,
      goRechargePackages,
      loadOverview,
      loadRecords,
      reloadRecordsFromFirstPage,
      exportCsv,
      changePage,
      saveOverview,
      scheduleOverviewSave,
      startEdit,
      cancelEdit,
      saveTemplate,
      toggleTemplateEnabled,
      canManualSend,
      manualSendTitle,
      manualSendIntro,
      openManualSend,
      closeManualSend,
      searchRecipients,
      changeRecipientPage,
      togglePageAll,
      submitManualSend,
      openResend,
      closeResend,
      submitResend,
      queryStatus,
      toggleExpanded,
      statusLabel,
      statusClass,
      isFailureStatus,
      canResend,
      detailReason,
      sourceLabel,
      audienceLabel,
      audienceOptions,
      recipientReasonLabel,
      timingLabel,
      timingSelectValue,
      timingOptions,
      onTimingChange,
      formatDateTime
    }
  }
}
</script>

<style scoped>
.sms-page{min-height:100%;padding:22px 24px 32px;background:#f4f6f9;color:#27364b;box-sizing:border-box}
.page-heading{display:flex;justify-content:space-between;align-items:flex-start;gap:16px;margin-bottom:18px}
.page-heading h1{display:flex;align-items:center;gap:9px;margin:0 0 6px;font-size:18px;color:#172338}
.title-bar{display:inline-block;width:3px;height:18px;flex:none;border-radius:2px;background:#4b7bec}
.page-heading p{margin:0;color:#8c9bb2;font-size:12px}
.gate-card{min-height:420px;display:flex;align-items:center;justify-content:center;border:1px solid #e7ebf2;border-radius:10px;background:#fff}
.tabs{display:inline-flex;padding:4px;margin-bottom:16px;border:1px solid #e4e9f1;border-radius:10px;background:#fff}
.tabs button,.status-tabs button{border:0;background:transparent;color:#56657b;cursor:pointer}
.tabs button{padding:8px 16px;border-radius:7px}
.tabs button.selected,.status-tabs button.selected,.primary-button{background:#4c78ef;color:#fff}
.records-panel{overflow:hidden;border:1px solid #e7ebf2;border-radius:10px;background:#fff}
.sms-rec-metrics{--border:#e5e7eb;--text3:#7a8797;display:grid;grid-template-columns:repeat(3,1fr);gap:14px;margin-bottom:14px;padding:16px 16px 0}
.sms-rec-metric{background:#fff;border:1px solid var(--border);border-radius:10px;padding:14px 16px}
.sms-rec-metric-v{font-size:22px;font-weight:700;color:#1a1a1a}.sms-rec-metric-v.failure{color:#ef4444}
.sms-rec-metric-l{font-size:12px;color:var(--text3);margin-top:4px}.sms-btn-sm{height:24px;margin-left:6px;padding:0 8px;border-radius:5px;font-size:11px;cursor:pointer}.sms-btn-sm.primary{border:1px solid #4c78ef;background:#4c78ef;color:#fff}
.sms-no-package{margin-top:6px;color:#e4911e;font-size:12px}
.content-card{padding:0 16px 10px}.status-tabs{display:flex;gap:7px;margin-bottom:12px}.status-tabs button{padding:7px 13px;border:1px solid #e7ebf1;border-radius:7px;background:#fff}.status-tabs button.selected{background:#4c78ef;color:#fff;border-color:#4c78ef}.status-tabs button.selected small{color:#fff}
.filters{display:flex;gap:8px;margin-bottom:12px;align-items:center;flex-wrap:wrap}.filters select{height:34px;border:1px solid #e1e6ee;border-radius:7px;background:#fff;color:#64738a;padding:0 9px;font-size:12px}
.plain-button{padding:0 12px;height:34px;border:1px solid #e1e6ee;border-radius:7px;background:#fff;color:#56657b;cursor:pointer;font-size:12px}.plain-button:disabled{opacity:.5;cursor:not-allowed}
.search-box{display:flex;align-items:center;gap:8px;width:240px;margin-left:auto;padding:0 12px;border:1px solid #e1e6ee;border-radius:7px;background:#fff;height:34px;box-sizing:border-box}.search-box input{display:block;width:100%;height:32px;margin:0;padding:0;border:0;outline:0;color:#334155;font-size:12px;line-height:32px;background:transparent}.search-box span{display:flex;width:18px;height:32px;flex:none;align-items:center;justify-content:center;font-size:14px;color:#93a1b6;line-height:1}
.error-state{display:flex;align-items:center;justify-content:space-between;margin-bottom:10px;padding:10px 12px;border:1px solid #ffd4d4;border-radius:7px;background:#fff6f6;color:#d23f48;font-size:12px}.error-state button{border:0;background:transparent;color:#4c78ef;cursor:pointer}
.table-wrap{overflow:auto;border:1px solid #e8ecf2;border-radius:8px}table{width:100%;border-collapse:collapse;font-size:12px}th,td{height:43px;padding:0 12px;border-bottom:1px solid #edf0f4;text-align:left;white-space:nowrap}th{height:38px;background:#fafbfd;color:#77869c;font-weight:500}td{color:#52647d}tbody tr:last-child td{border-bottom:0}tbody tr.failed{background:#fff8f8}
.type-pill{padding:4px 9px;border-radius:12px;font-size:11px}.type-pill.automatic{background:#eef5ff;color:#4d7df2}.type-pill.manual{background:#e9fbf3;color:#14a879}.type-pill.resend{background:#fff4e8;color:#d67a18}
.state.success{color:#0db982}.state.failure{color:#ef4b54}.state.pending{color:#e4911e}.muted{color:#9aa6b7}.link-button{margin-right:6px;padding:5px 10px;border:1px solid #4f7df1;border-radius:5px;background:#fff;color:#4f7df1;cursor:pointer;font-size:12px}.link-button.secondary{border-color:#d6b06b;color:#b57b20}.link-button:disabled{opacity:.55;cursor:wait}
.table-footer{display:flex;justify-content:space-between;align-items:center;padding:12px 4px;color:#95a1b2;font-size:12px}.pagination{display:flex;align-items:center;gap:8px}.pagination-summary{color:#8794a9;white-space:nowrap}.pagination button,.modal-pagination button{min-width:64px;height:32px;padding:0 12px;border:1px solid #dfe5ed;border-radius:7px;background:#fff;color:#52647d;font-size:12px;cursor:pointer}.pagination button:disabled,.modal-pagination button:disabled{background:#fafbfd;color:#b5becb;cursor:default}
.sms-overview{display:flex;align-items:center;justify-content:space-between;gap:20px;flex-wrap:wrap;margin:0 0 16px;padding:16px 20px;border:1px solid #e5eaf2;border-radius:10px;background:#fff}.sms-ov-info{display:flex;align-items:center;gap:16px}.sms-ov-title{font-size:14px;font-weight:600;color:#1a1a1a}.sms-ov-status{margin-top:5px;font-size:12px;color:#8491a4}.sms-ov-status .on{color:#11b982}.sms-ov-right{display:flex;align-items:center;gap:22px;flex-wrap:wrap}.sms-stat{display:flex;flex-direction:column;gap:4px}.sms-stat-v{font-size:22px;font-weight:700;color:#1a1a1a;line-height:1}.sms-stat-l{display:flex;align-items:center;gap:6px;font-size:12px;color:#8491a4}.sms-stat-input{display:flex;align-items:center;gap:6px;font-size:12px;color:#52647d}.sms-stat-input .inp{width:76px;height:32px;padding:0 8px;border:1px solid #dfe5ed;border-radius:6px;background:#fff;color:#34445b;font-size:12px;box-sizing:border-box}
.tog{position:relative;display:inline-flex;width:42px;height:24px;flex:none;cursor:pointer}.tog input{position:absolute;inset:0;width:100%;height:100%;margin:0;opacity:0;cursor:pointer}.tog-slider{position:absolute;inset:0;border-radius:12px;background:#d1d5db;transition:background .18s}.tog-slider:after{position:absolute;top:3px;left:3px;width:18px;height:18px;border-radius:50%;background:#fff;content:'';transition:transform .18s}.tog input:checked+.tog-slider{background:#4b7bec}.tog input:checked+.tog-slider:after{transform:translateX(18px)}.off-text{color:#9199a5}
.template-grid{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:12px;padding:14px;border:1px solid #e7ebf2;border-radius:10px;background:#fff}.template-card{padding:14px;border:1px solid #e2e7ef;border-radius:9px;background:#fff}.template-card>header{display:flex;align-items:center;gap:8px;margin-bottom:12px}.template-card>header strong{font-size:13px;color:#1e2c40}.template-card>header small{padding:3px 6px;border-radius:6px;background:#f0f2f6;color:#7e8a9c;font-size:10px;overflow:hidden;text-overflow:ellipsis}.green-dot{width:8px;height:8px;border-radius:50%;background:#10bd84}.green-dot.off{background:#c6ccd5}.switch{position:relative;display:inline-flex;width:38px;height:22px;margin-left:auto;cursor:pointer}.switch input{position:absolute;opacity:0;width:100%;height:100%;cursor:pointer}.switch i{width:38px;height:22px;border-radius:11px;background:#cbd1da;transition:.2s}.switch i:after{content:'';position:absolute;top:3px;left:3px;width:16px;height:16px;border-radius:50%;background:white;transition:.2s}.switch input:checked+i{background:#4d7cf0}.switch input:checked+i:after{transform:translateX(16px)}
.template-content{min-height:82px;margin:0;padding:10px;border:1px solid #e4e8ef;border-radius:7px;background:#fafbfd;color:#526780;font-size:12px;line-height:1.6;white-space:pre-wrap;font-family:inherit}.template-options{display:flex;flex-direction:column;gap:8px;margin-top:10px;padding:10px;border-radius:7px;background:#f8f9fb}.template-options label{display:flex;align-items:center;justify-content:space-between;gap:10px;font-size:11px;color:#718097}.template-options select{max-width:65%;height:30px;border:1px solid #e1e6ee;border-radius:6px;font-size:12px;background:#fff}.template-options .manual-check{justify-content:flex-start;align-self:flex-start;gap:5px}
.card-actions,.editing-actions{display:flex;gap:8px;margin-top:12px;padding-top:10px;border-top:1px solid #edf0f4}.primary-button{min-height:32px;padding:0 14px;border:1px solid #4c78ef;border-radius:6px;cursor:pointer;font-size:12px}.primary-button:disabled{opacity:.5;cursor:not-allowed}
.modal-backdrop{position:fixed;inset:0;z-index:3000;display:flex;align-items:center;justify-content:center;background:#17203366;padding:20px}.resend-modal,.manual-send-modal{width:min(620px,100%);max-height:min(720px,calc(100vh - 40px));display:flex;flex-direction:column;overflow:hidden;border-radius:10px;background:#fff;box-shadow:0 18px 55px #0b16352e}.resend-modal>header,.manual-send-modal>header{display:flex;justify-content:space-between;align-items:center;padding:16px 20px;border-bottom:1px solid #e9edf3}.resend-modal h2,.manual-send-modal h2{margin:0;font-size:15px;color:#1e2c40}.close-button{border:0;background:none;color:#8491a4;font-size:22px;cursor:pointer;line-height:1}
.manual-send-intro{padding:14px 16px 8px;color:#91a0b6;font-size:12px}.manual-send-body{padding:0 16px 12px;overflow:hidden}.manual-tools{display:flex;gap:8px}.manual-search{display:flex;align-items:center;flex:1;height:36px;padding:0 10px;border:1px solid #dfe5ee;border-radius:7px;color:#a0aec0}.manual-search input{width:100%;border:0;outline:0;font-size:12px;color:#334155}.manual-select-all{display:flex;align-items:center;justify-content:space-between;margin-top:10px;padding:9px 12px;border:1px solid #dfe5ee;border-radius:7px;color:#34445b;font-size:12px}.participant-list{max-height:360px;margin-top:8px;overflow:auto}.participant-row{display:grid;grid-template-columns:20px minmax(80px,1fr) 120px 90px;align-items:center;gap:8px;min-height:42px;margin-bottom:6px;padding:0 10px;border:1px solid #e3e8f0;border-radius:7px;color:#52647d;font-size:12px}.participant-row.disabled{background:#fafbfd;color:#9ba6b5}.participant-row strong{color:#1f2e43;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}.participant-row small{color:#8a96a8;text-align:right}.modal-pagination{display:flex;align-items:center;justify-content:flex-end;gap:8px;margin-top:10px;color:#8794a9;font-size:12px}.manual-send-modal>footer,.resend-modal>footer{display:flex;justify-content:flex-end;gap:8px;padding:14px 20px;border-top:1px solid #e9edf3}
.modal-body{display:flex;flex-direction:column;gap:16px;padding:22px 20px}.modal-body>div,.modal-body>label{display:grid;grid-template-columns:82px 1fr;gap:10px;font-size:12px}.modal-body span{color:#91a0b6}.modal-body strong{font-weight:500;color:#34445b}.modal-body input{height:34px;padding:0 10px;border:1px solid #e0e6ee;border-radius:7px;font-size:12px}.error-text{color:#ef4b54!important}
.detail-row td{height:auto;padding:10px 12px 10px 46px;background:#fffcfc;white-space:normal;line-height:1.55}.detail-reason{margin-bottom:4px;color:#ef4b54}.detail-reason strong,.detail-content strong{font-weight:600}.detail-content{color:#52647d}.empty-cell{height:80px;text-align:center;color:#98a3b2}
@media (max-width:1200px){.template-grid{grid-template-columns:repeat(2,minmax(0,1fr))}}@media (max-width:760px){.sms-page{padding:14px}.sms-rec-metrics,.template-grid{grid-template-columns:1fr}.sms-overview{align-items:flex-start}.sms-ov-right{width:100%;justify-content:space-between}.search-box{width:100%;margin-left:0}.participant-row{grid-template-columns:20px 1fr 110px}.participant-row small{grid-column:2/4}.page-heading{flex-direction:column}}
</style>
